import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import NetworkCanvas from "@/components/NetworkCanvas";
import { useLang } from "@/i18n";

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex flex-col justify-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 opacity-60">
        <NetworkCanvas />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-transparent to-[#050505]" />

      <motion.div style={{ y, opacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full pt-32 pb-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="font-mono text-xs uppercase tracking-[0.4em] text-neutral-400 mb-8"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="font-display font-black tracking-tighter leading-[0.9] text-[clamp(3.5rem,11vw,9rem)]" data-testid="hero-heading">
          {t.hero.lines.map((line, i) => (
            <span key={line} className="mask-line">
              <motion.span
                className={`block ${i === 1 ? "text-brand-light" : "text-white"}`}
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.35 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-12 flex flex-col lg:flex-row lg:items-end gap-10 lg:gap-24">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="max-w-md text-neutral-400 text-base leading-relaxed"
            data-testid="hero-subheading"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7, duration: 0.8 }}
            className="flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-quote-button"
              className="group inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-white font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 rounded-full transition-[background-color,transform,box-shadow] duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(224,18,127,0.5)]"
            >
              {t.cta}
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#services"
              data-testid="hero-services-link"
              className="group inline-flex items-center gap-2 border border-white/20 hover:border-brand-light text-neutral-300 hover:text-white font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 rounded-full transition-[border-color,color,transform] duration-300 hover:scale-105"
            >
              {t.hero.explore}
              <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="mt-20 grid grid-cols-3 max-w-2xl border-t border-white/10"
          data-testid="hero-stats"
        >
          {t.hero.stats.map((s) => (
            <div key={s.label} className="pt-6 pr-6">
              <p className="font-display text-3xl md:text-4xl font-800 font-bold text-white">{s.value}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-2">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
